import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// We track which entry the user has seen via the entry id, so a NEW
// changelog entry from the admin will re-trigger the banner automatically.
const STORAGE_KEY = 'changelog_seen_id_v1';

export default function WhatsNewBanner() {
  const [open, setOpen] = useState(false);
  const [entry, setEntry] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let pollAttempts = 0;
    const MAX_POLL_ATTEMPTS = 6; // ~3s of 500ms polls

    const tryShow = async () => {
      try {
        if (typeof window === 'undefined' || cancelled) return;
        const hasAuth = localStorage.getItem('auth_token') || localStorage.getItem('token');
        if (!hasAuth) {
          // Auth token may still be writing — poll briefly before giving up
          if (pollAttempts < MAX_POLL_ATTEMPTS) {
            pollAttempts += 1;
            setTimeout(tryShow, 500);
          }
          return;
        }

        // Don't compete with the 4-step Dashboard walkthrough during a
        // user's first session. The walkthrough sets isp_walkthrough_v2_done
        // when finished/dismissed; until then this banner stays quiet.
        if (!localStorage.getItem('isp_walkthrough_v2_done')) return;

        const res = await axios.get(`${API}/changelog/latest`);
        const latest = res?.data?.entry;
        if (!latest || cancelled) return;

        const seenId = localStorage.getItem(STORAGE_KEY);
        if (seenId === latest.id) return;

        setEntry(latest);
        setTimeout(() => {
          if (!cancelled) setOpen(true);
        }, 600);
      } catch {
        // Silently ignore — banner is non-critical
      }
    };

    // Initial fire after small delay so post-login navigation can settle
    const initial = setTimeout(tryShow, 800);
    // Also react to auth_token being written by another tab/component
    const onStorage = (e) => {
      if (e.key === 'auth_token' && e.newValue) tryShow();
    };
    window.addEventListener('storage', onStorage);

    return () => {
      cancelled = true;
      clearTimeout(initial);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const dismiss = () => {
    try {
      if (entry?.id) localStorage.setItem(STORAGE_KEY, entry.id);
    } catch {}
    setOpen(false);
  };

  if (!entry) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-4 left-4 z-[60] max-w-md"
          data-testid="whats-new-banner"
          style={{ fontFamily: 'var(--sf-text)' }}
        >
          <div className="relative bg-white border border-[#0A0A0A] shadow-[0_24px_48px_-12px_rgba(0,0,0,0.18)]">
            {/* Top accent bar */}
            <div className="h-1 bg-[#002FA7]" />

            <div className="p-5 sm:p-6">
              <button
                onClick={dismiss}
                aria-label="Dismiss"
                className="absolute top-3 right-3 p-1.5 text-[#A1A1AA] hover:text-[#0A0A0A] transition-colors"
                data-testid="whats-new-dismiss"
              >
                <X className="w-4 h-4" strokeWidth={1.75} />
              </button>

              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-3.5 h-3.5 text-[#002FA7]" strokeWidth={2} />
                <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#52525B]">
                  — What's new / {entry.version}
                </span>
              </div>

              <h3
                className="text-2xl font-extrabold text-[#0A0A0A] leading-[1.05] tracking-[-0.025em] mb-3"
                style={{ fontFamily: 'var(--sf-display)' }}
                data-testid="whats-new-title"
              >
                {entry.title}
              </h3>

              {entry.body && (
                <p className="text-sm text-[#52525B] leading-relaxed mb-5">
                  {entry.body}
                </p>
              )}

              {Array.isArray(entry.highlights) && entry.highlights.length > 0 && (
                <ul className="space-y-3 mb-6">
                  {entry.highlights.map((h, i) => (
                    <li
                      key={i}
                      className="flex gap-3 pb-3 border-b border-[#E4E4E7] last:border-b-0 last:pb-0"
                    >
                      <span className="text-[10px] font-mono text-[#A1A1AA] tabular-nums pt-0.5">
                        0{i + 1}
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-[#0A0A0A]">{h.title}</p>
                        <p className="text-xs text-[#52525B] mt-0.5 leading-relaxed">{h.body}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              <div className="flex items-center justify-between gap-3">
                <button
                  onClick={dismiss}
                  className="text-xs font-medium text-[#52525B] hover:text-[#0A0A0A] transition-colors"
                  data-testid="whats-new-skip"
                >
                  Don't show again
                </button>
                <a
                  href="/changelog"
                  onClick={dismiss}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#002FA7] text-white text-xs font-medium hover:bg-[#00227A] transition-colors"
                  data-testid="whats-new-cta"
                >
                  Read full notes
                  <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
